// Resource loader for textures and models
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

export interface LoadedResources {
    textures: Map<string, THREE.Texture>;
    models: Map<string, THREE.Group>;
}

export class Resources {
    private static instance: Resources;

    private textureLoader: THREE.TextureLoader;
    private gltfLoader: GLTFLoader;
    public textures: Map<string, THREE.Texture> = new Map();
    public models: Map<string, THREE.Group> = new Map();

    private constructor() {
        this.textureLoader = new THREE.TextureLoader();
        this.gltfLoader = new GLTFLoader();
    }

    static getInstance(): Resources {
        if (!Resources.instance) {
            Resources.instance = new Resources();
        }
        return Resources.instance;
    }

    async loadTexture(name: string, path: string): Promise<THREE.Texture> {
        return new Promise((resolve, _reject) => {
            this.textureLoader.load(
                path,
                (texture) => {
                    this.textures.set(name, texture);
                    resolve(texture);
                },
                undefined,
                (error) => {
                    console.warn(`Failed to load texture: ${name}`, error);
                    // Return placeholder
                    const placeholder = this.createPlaceholderTexture();
                    this.textures.set(name, placeholder);
                    resolve(placeholder);
                }
            );
        });
    }

    async loadModel(name: string, path: string): Promise<THREE.Group> {
        return new Promise((resolve, reject) => {
            this.gltfLoader.load(
                path,
                (gltf) => {
                    this.models.set(name, gltf.scene);
                    resolve(gltf.scene);
                },
                undefined,
                (error) => {
                    console.warn(`Failed to load model: ${name}`, error);
                    reject(error);
                }
            );
        });
    }

    /**
     * Attempt to load a building as a 3D GLB model
     * Returns the model if found, null if not found (fallback to procedural)
     */
    async loadBuildingAsset(buildingType: string): Promise<THREE.Group | null> {
        const glbPath = `/assets/${buildingType}.glb`;

        try {
            const model = await this.loadModel(`building_${buildingType}`, glbPath);
            console.log(`✓ Loaded 3D model for building: ${buildingType}`);
            return model;
        } catch (error) {
            // GLB not found - will use procedural fallback
            console.log(`→ Using procedural generation for building: ${buildingType}`);
            return null;
        }
    }

    private createPlaceholderTexture(): THREE.Texture {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext('2d')!;

        // Pink/magenta checkerboard for missing texture
        ctx.fillStyle = '#ff00ff';
        ctx.fillRect(0, 0, 64, 64);
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, 32, 32);
        ctx.fillRect(32, 32, 32, 32);

        return new THREE.CanvasTexture(canvas);
    }

    getTexture(name: string): THREE.Texture | undefined {
        return this.textures.get(name);
    }

    getModel(name: string): THREE.Group | undefined {
        return this.models.get(name)?.clone();
    }
}
